import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Camera, MapPin, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'all', label: 'All Photos' },
  { value: 'tiger', label: 'Tigers' },
  { value: 'leopard', label: 'Leopards' },
  { value: 'birds', label: 'Birds' },
  { value: 'wildlife', label: 'Other Wildlife' },
  { value: 'landscape', label: 'Landscapes' },
  { value: 'group', label: 'Happy Guests' },
];

const resolveImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

const SafariLibrary = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/gallery`);
        setItems(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const filtered = useMemo(() => {
    if (category === 'all') return items;
    return items.filter((i) => i.category === category);
  }, [items, category]);

  const open = (idx) => setLightboxIdx(idx);
  const close = () => setLightboxIdx(null);
  const prev = () => setLightboxIdx((i) => (i - 1 + filtered.length) % filtered.length);
  const next = () => setLightboxIdx((i) => (i + 1) % filtered.length);

  return (
    <div className="bg-sand min-h-screen pt-20" data-testid="safari-library-page">
      {/* Hero */}
      <section className="py-20 md:py-28 px-4 bg-navy">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="font-accent text-xs uppercase tracking-[0.3em] text-gold/80 mb-4 block">
              Captured by Our Guests
            </span>
            <h1 className="font-heading text-5xl md:text-7xl font-light text-gold mb-6 leading-[0.95]">
              Safari Library
            </h1>
            <p className="font-body text-base md:text-lg text-sand/80 max-w-2xl mx-auto leading-relaxed">
              Real moments. Real wildlife. Real guests. Browse memories captured by travellers
              who experienced Tadoba's wild magic with JMD Wildlife.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category filters */}
      <section className="py-10 px-4 border-b border-emerald-100">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-center gap-3">
          {CATEGORIES.map((c) => (
            <button
              key={c.value}
              onClick={() => setCategory(c.value)}
              data-testid={`gallery-filter-${c.value}`}
              className={`px-5 py-2 rounded-full font-body text-sm font-medium transition-all ${
                category === c.value
                  ? 'bg-navy text-gold'
                  : 'bg-white text-navy hover:bg-emerald-50 border border-emerald-100'
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </section>

      {/* Masonry grid */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <p className="text-center font-body text-muted-foreground py-16">Loading gallery…</p>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl shadow-md max-w-xl mx-auto">
              <Camera size={48} className="mx-auto text-emerald-300 mb-4" />
              <p className="font-body text-navy text-lg">
                {category === 'all'
                  ? 'Photos coming soon. Be part of our story — book your safari today.'
                  : 'No photos in this category yet.'}
              </p>
            </div>
          ) : (
            <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
              {filtered.map((item, idx) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: (idx % 8) * 0.04 }}
                  onClick={() => open(idx)}
                  className="break-inside-avoid relative rounded-xl overflow-hidden shadow-lg group cursor-pointer bg-white"
                  data-testid={`library-tile-${idx}`}
                >
                  <img
                    src={resolveImageUrl(item.image_url)}
                    alt={item.caption || 'Safari moment'}
                    className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  {item.featured && (
                    <div className="absolute top-3 left-3 bg-amber-400 text-emerald-900 text-[10px] font-bold px-2 py-1 rounded-full tracking-wider">
                      ★ FEATURED
                    </div>
                  )}
                  {(item.caption || item.customer_name) && (
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent text-white p-4 opacity-0 group-hover:opacity-100 transition">
                      {item.caption && (
                        <p className="font-body text-sm font-medium leading-tight mb-1">
                          {item.caption}
                        </p>
                      )}
                      {item.customer_name && (
                        <p className="font-accent text-xs text-amber-300">— {item.customer_name}</p>
                      )}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      {filtered.length > 0 && (
        <section className="py-16 px-4 bg-navy">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-5xl text-gold mb-4 leading-tight">
              Ready to make your own story?
            </h2>
            <p className="font-body text-sand/80 mb-6">
              Book your Tadoba safari and become part of the JMD Wildlife Library.
            </p>
            <a
              href="/packages"
              className="inline-flex items-center bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 rounded-full font-body font-bold tracking-wide transition-all hover:scale-105"
            >
              Explore Safari Packages
            </a>
          </div>
        </section>
      )}

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIdx !== null && filtered[lightboxIdx] && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={close}
            data-testid="lightbox"
          >
            <button
              onClick={close}
              className="absolute top-5 right-5 text-white hover:text-amber-400 z-10"
            >
              <X size={32} />
            </button>
            {filtered.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prev(); }}
                  className="absolute left-5 top-1/2 -translate-y-1/2 text-white hover:text-amber-400 z-10"
                >
                  <ChevronLeft size={40} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); next(); }}
                  className="absolute right-5 top-1/2 -translate-y-1/2 text-white hover:text-amber-400 z-10"
                >
                  <ChevronRight size={40} />
                </button>
              </>
            )}
            <div
              onClick={(e) => e.stopPropagation()}
              className="max-w-5xl w-full max-h-[90vh] flex flex-col items-center"
            >
              <img
                src={resolveImageUrl(filtered[lightboxIdx].image_url)}
                alt={filtered[lightboxIdx].caption || 'Safari'}
                className="max-h-[75vh] w-auto object-contain rounded-lg shadow-2xl"
              />
              {(filtered[lightboxIdx].caption ||
                filtered[lightboxIdx].customer_name ||
                filtered[lightboxIdx].location ||
                filtered[lightboxIdx].safari_date) && (
                <div className="mt-4 bg-white/10 backdrop-blur-md rounded-lg px-6 py-4 max-w-2xl text-center">
                  {filtered[lightboxIdx].caption && (
                    <p className="font-heading text-lg text-amber-300 mb-2">
                      {filtered[lightboxIdx].caption}
                    </p>
                  )}
                  <div className="flex flex-wrap justify-center gap-4 text-white/80 font-body text-sm">
                    {filtered[lightboxIdx].customer_name && (
                      <span>👤 {filtered[lightboxIdx].customer_name}</span>
                    )}
                    {filtered[lightboxIdx].location && (
                      <span className="flex items-center"><MapPin size={14} className="mr-1" />{filtered[lightboxIdx].location}</span>
                    )}
                    {filtered[lightboxIdx].safari_date && (
                      <span className="flex items-center"><Calendar size={14} className="mr-1" />{filtered[lightboxIdx].safari_date}</span>
                    )}
                  </div>
                  <p className="text-white/40 text-xs mt-2">
                    {lightboxIdx + 1} / {filtered.length}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SafariLibrary;
